import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

// Mock clip data
const MOCK_CLIP = {
  id: 'clip-1',
  title: 'Logo Bounce Intro',
  projectName: 'Logo Animations',
  duration: 3.2,
  width: 1920,
  height: 1080,
  fps: 30,
  status: 'completed' as const,
  tags: ['bounce', 'scale', 'ease-out'],
  createdAt: '2 hours ago',
};

// Mock generated code
const MOCK_GENERATED_CODE = `import { useCurrentFrame, interpolate, AbsoluteFill, Easing } from 'remotion';

/**
 * Logo Bounce Animation
 * Generated by VideoToCode from video analysis
 *
 * Detected patterns:
 * - Primary motion: bounce with scale
 * - Easing: ease-out (detected 87% confidence)
 * - Total duration: 96 frames (3.2s @ 30fps)
 */

export const LogoBounce: React.FC = () => {
  const frame = useCurrentFrame();

  // Position interpolation from object tracking
  const x = interpolate(
    frame,
    [0, 24, 48, 72, 96],
    [100, 250, 400, 550, 700],
    {
      extrapolateRight: 'clamp',
      easing: Easing.out(Easing.cubic),
    }
  );

  const y = interpolate(
    frame,
    [0, 12, 24, 36, 48, 60, 72, 84, 96],
    [300, 150, 300, 200, 300, 250, 300, 275, 300],
    {
      extrapolateRight: 'clamp',
      easing: Easing.bounce,
    }
  );

  // Scale animation (detected from bounding box analysis)
  const scale = interpolate(
    frame,
    [0, 24, 48, 96],
    [0.5, 1.2, 1, 1],
    {
      extrapolateRight: 'clamp',
      easing: Easing.out(Easing.back(1.5)),
    }
  );

  // Rotation (subtle wobble detected)
  const rotation = interpolate(
    frame,
    [0, 12, 24, 36, 48],
    [0, -5, 0, 3, 0],
    {
      extrapolateRight: 'clamp',
    }
  );

  // Opacity fade-in
  const opacity = interpolate(frame, [0, 15], [0, 1], {
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0a0b',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          transform: \`
            translateX(\${x}px)
            translateY(\${y}px)
            scale(\${scale})
            rotate(\${rotation}deg)
          \`,
          opacity,
          width: 120,
          height: 120,
          borderRadius: 24,
          background: 'linear-gradient(135deg, #06d6a0, #8b5cf6)',
          boxShadow: '0 20px 60px rgba(6, 214, 160, 0.3)',
        }}
      />
    </AbsoluteFill>
  );
};

// Remotion composition config
export const config = {
  id: 'LogoBounce',
  fps: 30,
  width: 1920,
  height: 1080,
  durationInFrames: 96,
};
`;

// Mock analysis data
const MOCK_ANALYSIS = {
  objectTracking: {
    tracks: 1,
    frames: 96,
    avgConfidence: 0.94,
  },
  colorExtraction: {
    dominantColors: ['#06d6a0', '#8b5cf6', '#0a0a0b'],
    colorShifts: 2,
  },
  opticalFlow: {
    avgMagnitude: 12.4,
    dominantDirection: 'right',
    peakMotionFrame: 24,
  },
  animationClassifier: {
    types: ['bounce', 'scale', 'fade-in'],
    easing: 'ease-out',
    confidence: 0.87,
  },
};

type Tab = 'code' | 'analysis' | 'settings';
type AnalysisStatus = 'idle' | 'processing' | 'completed' | 'failed';

export default function EditorPage() {
  const { clipId: _clipId } = useParams(); // Will be used when connecting to real API
  const [activeTab, setActiveTab] = useState<Tab>('code');
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>('completed');
  const [analysisProgress, setAnalysisProgress] = useState(100);
  const [copied, setCopied] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Simulate video playback
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isPlaying) {
      interval = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= MOCK_CLIP.duration) {
            setIsPlaying(false);
            return 0;
          }
          return prev + 0.1;
        });
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(MOCK_GENERATED_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleReanalyze = () => {
    setAnalysisStatus('processing');
    setAnalysisProgress(0);

    // Simulate progress
    const interval = setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setAnalysisStatus('completed');
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 500);
  };

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 64px)',
        overflow: 'hidden',
      }}
    >
      {/* Left Panel - Video Preview */}
      <div
        style={{
          width: '50%',
          borderRight: '1px solid var(--black-700)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Video Header */}
        <div
          style={{
            padding: 'var(--space-lg)',
            borderBottom: '1px solid var(--black-700)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-xs)' }}>
              <Link
                to="/dashboard"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  color: 'var(--black-300)',
                  fontSize: '0.8125rem',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </Link>
            </div>
            <h3 style={{ marginBottom: 'var(--space-xs)' }}>{MOCK_CLIP.title}</h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--black-400)' }}>
              {MOCK_CLIP.projectName} • {MOCK_CLIP.width}×{MOCK_CLIP.height} • {MOCK_CLIP.fps}fps
            </p>
          </div>

          {/* Tags */}
          <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
            {MOCK_CLIP.tags.map((tag) => (
              <span
                key={tag}
                className="badge badge-cyan"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Video Player */}
        <div
          style={{
            flex: 1,
            background: 'var(--black-900)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Video placeholder with animated element */}
          <div
            style={{
              width: '80%',
              aspectRatio: '16/9',
              background: 'var(--black-800)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Animated logo preview */}
            <div
              style={{
                width: 60,
                height: 60,
                borderRadius: 12,
                background: 'linear-gradient(135deg, var(--cyan-500), var(--purple-500))',
                boxShadow: '0 10px 40px var(--cyan-glow)',
                transform: `translateX(${Math.sin(currentTime * 2) * 50}px) translateY(${Math.abs(Math.sin(currentTime * 4)) * -30}px) scale(${1 + Math.sin(currentTime * 3) * 0.1})`,
                transition: isPlaying ? 'none' : 'transform 0.3s ease',
              }}
            />

            {/* Scan line effect */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)',
                pointerEvents: 'none',
              }}
            />
          </div>

          {/* Analysis overlay during processing */}
          {analysisStatus === 'processing' && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-lg)',
              }}
            >
              <div
                style={{
                  width: 64,
                  height: 64,
                  border: '3px solid var(--black-600)',
                  borderTopColor: 'var(--cyan-500)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: 'white', marginBottom: 'var(--space-sm)' }}>Analyzing video...</p>
                <p style={{ color: 'var(--black-400)', fontSize: '0.875rem' }}>
                  {analysisProgress < 30
                    ? 'Extracting frames...'
                    : analysisProgress < 60
                    ? 'Tracking objects...'
                    : analysisProgress < 90
                    ? 'Classifying animations...'
                    : 'Generating code...'}
                </p>
              </div>
              <div style={{ width: 200 }} className="progress-bar">
                <div className="progress-fill" style={{ width: `${Math.min(analysisProgress, 100)}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Video Controls */}
        <div
          style={{
            padding: 'var(--space-lg)',
            borderTop: '1px solid var(--black-700)',
            background: 'var(--black-800)',
          }}
        >
          {/* Timeline */}
          <div style={{ marginBottom: 'var(--space-md)' }}>
            <div
              style={{
                height: 40,
                background: 'var(--black-700)',
                borderRadius: 'var(--radius-sm)',
                position: 'relative',
                cursor: 'pointer',
              }}
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const percent = x / rect.width;
                setCurrentTime(percent * MOCK_CLIP.duration);
              }}
            >
              {/* Waveform-like decoration */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-around',
                  padding: '0 8px',
                }}
              >
                {Array.from({ length: 50 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: 2,
                      height: `${20 + Math.sin(i * 0.3) * 15 + Math.random() * 10}%`,
                      background: 'var(--black-500)',
                      borderRadius: 1,
                    }}
                  />
                ))}
              </div>

              {/* Progress */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${(currentTime / MOCK_CLIP.duration) * 100}%`,
                  background: 'rgba(6, 214, 160, 0.2)',
                  borderRadius: 'var(--radius-sm)',
                }}
              />

              {/* Playhead */}
              <div
                style={{
                  position: 'absolute',
                  left: `${(currentTime / MOCK_CLIP.duration) * 100}%`,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: 'var(--cyan-500)',
                  boxShadow: '0 0 10px var(--cyan-500)',
                }}
              />
            </div>
          </div>

          {/* Controls Row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
              {/* Play/Pause */}
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: 'var(--cyan-500)',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--black-900)',
                  transition: 'all var(--transition-fast)',
                }}
              >
                {isPlaying ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 2 }}>
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                )}
              </button>

              {/* Time display */}
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.875rem',
                  color: 'var(--black-200)',
                }}
              >
                <span style={{ color: 'white' }}>{currentTime.toFixed(1)}s</span>
                <span> / {MOCK_CLIP.duration.toFixed(1)}s</span>
              </div>
            </div>

            {/* Frame counter */}
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.8125rem',
                color: 'var(--black-400)',
              }}
            >
              Frame {Math.floor(currentTime * MOCK_CLIP.fps)} / {Math.floor(MOCK_CLIP.duration * MOCK_CLIP.fps)}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Code & Analysis */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--black-900)',
        }}
      >
        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--black-700)',
            background: 'var(--black-800)',
          }}
        >
          <TabButton active={activeTab === 'code'} onClick={() => setActiveTab('code')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            Generated Code
          </TabButton>
          <TabButton active={activeTab === 'analysis'} onClick={() => setActiveTab('analysis')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            Analysis
          </TabButton>
          <TabButton active={activeTab === 'settings'} onClick={() => setActiveTab('settings')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            Settings
          </TabButton>

          {/* Action buttons */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm)' }}>
            <button
              onClick={handleReanalyze}
              className="btn btn-ghost btn-sm"
              disabled={analysisStatus === 'processing'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
              Re-analyze
            </button>
            <button
              onClick={handleCopyCode}
              className="btn btn-primary btn-sm"
              disabled={analysisStatus !== 'completed'}
            >
              {copied ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy Code
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {activeTab === 'code' && (
            <div style={{ height: '100%' }}>
              <pre
                style={{
                  margin: 0,
                  padding: 'var(--space-xl)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.8125rem',
                  lineHeight: 1.7,
                  color: 'var(--black-100)',
                  height: '100%',
                  overflow: 'auto',
                }}
              >
                <code>{MOCK_GENERATED_CODE}</code>
              </pre>
            </div>
          )}

          {activeTab === 'analysis' && (
            <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
              <AnalysisCard title="Object Tracking" icon="📍">
                <StatRow label="Tracked objects" value={MOCK_ANALYSIS.objectTracking.tracks} />
                <StatRow label="Total frames" value={MOCK_ANALYSIS.objectTracking.frames} />
                <StatRow label="Avg confidence" value={`${(MOCK_ANALYSIS.objectTracking.avgConfidence * 100).toFixed(0)}%`} />
              </AnalysisCard>

              <AnalysisCard title="Color Analysis" icon="🎨">
                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--black-300)' }}>Dominant colors</span>
                  <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
                    {MOCK_ANALYSIS.colorExtraction.dominantColors.map((color) => (
                      <div
                        key={color}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 'var(--radius-sm)',
                          background: color,
                          border: '1px solid var(--black-600)',
                        }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
                <StatRow label="Color shifts detected" value={MOCK_ANALYSIS.colorExtraction.colorShifts} />
              </AnalysisCard>

              <AnalysisCard title="Motion Analysis" icon="🌊">
                <StatRow label="Avg motion magnitude" value={MOCK_ANALYSIS.opticalFlow.avgMagnitude.toFixed(1)} />
                <StatRow label="Dominant direction" value={MOCK_ANALYSIS.opticalFlow.dominantDirection} />
                <StatRow label="Peak motion frame" value={MOCK_ANALYSIS.opticalFlow.peakMotionFrame} />
              </AnalysisCard>

              <AnalysisCard title="Animation Classification" icon="✨">
                <div style={{ marginBottom: 'var(--space-md)' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--black-300)' }}>Detected types</span>
                  <div style={{ display: 'flex', gap: 'var(--space-xs)', marginTop: 'var(--space-sm)', flexWrap: 'wrap' }}>
                    {MOCK_ANALYSIS.animationClassifier.types.map((type) => (
                      <span key={type} className="badge badge-cyan">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
                <StatRow label="Detected easing" value={MOCK_ANALYSIS.animationClassifier.easing} />
                <StatRow label="Classification confidence" value={`${(MOCK_ANALYSIS.animationClassifier.confidence * 100).toFixed(0)}%`} />
              </AnalysisCard>
            </div>
          )}

          {activeTab === 'settings' && (
            <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
              <div>
                <h4 style={{ marginBottom: 'var(--space-md)' }}>Output Settings</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  <SettingRow label="Output FPS">
                    <select className="input" style={{ width: 120 }}>
                      <option value="24">24 fps</option>
                      <option value="30" selected>30 fps</option>
                      <option value="60">60 fps</option>
                    </select>
                  </SettingRow>
                  <SettingRow label="Output Resolution">
                    <select className="input" style={{ width: 160 }}>
                      <option value="720p">1280×720</option>
                      <option value="1080p" selected>1920×1080</option>
                      <option value="4k">3840×2160</option>
                    </select>
                  </SettingRow>
                  <SettingRow label="Include colors">
                    <ToggleSwitch defaultChecked />
                  </SettingRow>
                  <SettingRow label="Include easing curves">
                    <ToggleSwitch defaultChecked />
                  </SettingRow>
                </div>
              </div>

              <div>
                <h4 style={{ marginBottom: 'var(--space-md)' }}>Code Style</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  <SettingRow label="Add comments">
                    <ToggleSwitch defaultChecked />
                  </SettingRow>
                  <SettingRow label="TypeScript">
                    <ToggleSwitch defaultChecked />
                  </SettingRow>
                  <SettingRow label="Export config">
                    <ToggleSwitch defaultChecked />
                  </SettingRow>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-sm)',
        padding: 'var(--space-md) var(--space-lg)',
        fontSize: '0.875rem',
        fontWeight: 500,
        color: active ? 'white' : 'var(--black-300)',
        background: active ? 'var(--black-900)' : 'transparent',
        border: 'none',
        borderBottom: active ? '2px solid var(--cyan-500)' : '2px solid transparent',
        cursor: 'pointer',
        transition: 'all var(--transition-fast)',
      }}
    >
      {children}
    </button>
  );
}

function AnalysisCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: 'var(--space-lg)',
        background: 'var(--black-800)',
        border: '1px solid var(--black-700)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
        <span style={{ fontSize: '1.25rem' }}>{icon}</span>
        <h4>{title}</h4>
      </div>
      {children}
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: 'var(--space-sm) 0',
        borderBottom: '1px solid var(--black-700)',
      }}
    >
      <span style={{ fontSize: '0.875rem', color: 'var(--black-300)' }}>{label}</span>
      <span style={{ fontSize: '0.875rem', fontFamily: 'var(--font-mono)', color: 'white' }}>{value}</span>
    </div>
  );
}

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-sm) 0',
      }}
    >
      <span style={{ fontSize: '0.875rem', color: 'var(--black-200)' }}>{label}</span>
      {children}
    </div>
  );
}

function ToggleSwitch({ defaultChecked }: { defaultChecked?: boolean }) {
  const [checked, setChecked] = useState(defaultChecked ?? false);

  return (
    <button
      onClick={() => setChecked(!checked)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: checked ? 'var(--cyan-500)' : 'var(--black-600)',
        border: 'none',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background var(--transition-fast)',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: 'white',
          transition: 'left var(--transition-fast)',
        }}
      />
    </button>
  );
}
